import { useCallback } from 'react';
import { useAzureProvider } from 'src/azure/context';
import { useAutomationRuleFormProvider } from 'src/pages/automation-rule/form';
import { actionTypes } from 'src/pages/automation-rule/form/actions';
import { useAutomationRuleActions } from 'src/pages/automation-rule/form/actions/use-actions';
import { conditionTypes, useAutomationRuleConditions } from 'src/pages/automation-rule/form/conditions/use-conditions';
import useFieldValueChanged, { initialFieldValueChange } from 'src/pages/automation-rule/form/trigger/field-value-changed-form/use-field-value-changed';
import { triggerTypes, useAutomationRuleTrigger } from 'src/pages/automation-rule/form/trigger/use-trigger';
import { randomStringGenerator } from 'src/pages/automation-rule/form/utils/random-string-generator';

export function useResolveParentsByChildren() {
	const {applicationContext} = useAzureProvider();

	const { setTitle } = useAutomationRuleFormProvider();
	const { setTrigger, setTriggerData, trigger, triggerData } = useAutomationRuleTrigger();
	const { setConditions } = useAutomationRuleConditions();
	const { setActions} = useAutomationRuleActions();
	const { getValueOptions } = useFieldValueChanged();
  
	const resolveParentsByChildren = useCallback(async () => {
		setTitle('When all child work items are resolved, then resolve parent');
		setTrigger(triggerTypes.fieldValueIsChanged);
		setTriggerData({
			workItemTransitions: null,
			workItemIsAssignedTo: null,
			checklistTrigger: null,
			fieldValueChange: {
				...initialFieldValueChange,
				fieldNameQuery: null,
				fieldName: 'System.State',
				selectedOptions: 'System.State',
				fieldOptions: await getValueOptions({projectId: applicationContext.projectId}),
				loading: false,
			},
		});
		setConditions([{
			id: randomStringGenerator(),
			created_at: new Date(),
			condition: conditionTypes.fieldMeetACertainCriteria,
			checklistCompletedOrNotCompletedCondition: null,
			fieldMeetCertainCriteria: {
				relationship: 'sibling-work-items',
				relationshipCheck: 'all',
				workItemField: 'System.State',
				operator: 'SupportedOperations.Equals',
				inputValueType: 'select',
				selectValue: ['Done'],
				textValue: '',
				dateTimeValue: {
					type: 'datetime',
					datetime: {
						value: new Date(),
					},
					timeframe: {
						timeNumber: 0,
						timeUnit: 'minutes',
						timeOption: 'ago',
					},
				},
				userFieldValue: [],
				iterationOrAreaPathValue: '',
			},
		},
		]);
		setActions([
			{
				id: randomStringGenerator(),
				action: actionTypes.editWorkItem,
				editWorkItem: {
					workItemField: 'System.State',
					relationship: 'parent-work-item',
					inputValueType: 'select',
					selectValue: ['Done'],
					textValue: '',
					dateTimeValue: {
						type: 'datetime',
						datetime: {
							value: new Date(),
						},
						timeframe: {
							timeNumber: 0,
							timeUnit: 'minutes',
							timeOption: 'ago'
						}
					},
					userFieldValue: [],
					iterationOrAreaPathValue: '',
					copyFieldValue: '',
					fieldActionValue: 'set-value'
				},
				addCommentToWorkItem: null,
				sendNotificationToSlack: null,
				sendEmailNotification: null,
				sendTeamsNotification: null,
				createdAt: new Date()
			},
		]);
	}, [
		applicationContext.projectId,
		trigger,
		getValueOptions,
		setTitle,
		setTrigger,
		setTriggerData,
		setConditions,
		setActions,
		randomStringGenerator,
		trigger,
		triggerData,
	]);

	return {
		resolveParentsByChildren,
	};
}
